import { DiscoverResult, loadStripeTerminal, StripeTerminal, Terminal } from "@stripe/terminal-js";
import { isStripeEnabled } from "common";
import { omit, set } from "lodash";
import { useEffect, useRef } from "react";
import { EmvDevice, FeatureRole, Seller, StripeConnectionToken } from "types";
import { api } from "../api/api";
import { hasFeature } from "../models/Seller";
import { useCreatePurchaseStore } from "../stores/createPurchaseStore";
import { useSeller } from "./useAccount";

let readerTerminal: Terminal;
let isConnectingGlobal = false;

window.simulateStripe = () => {
    if (!window.flags) {
        window.flags = {};
    }

    set(window.flags, "stripe.simulateTerminal", true);
    console.log("%cStripe payments with EMV devices will now be simulated!", "color:green", window.flags);
};

export const useStripeTerminal = () => {
    const seller = useSeller();

    const isManual = useCreatePurchaseStore((state) => !state.isEMV);
    const hasConnected = useRef(false);

    // Initialize the stripe terminal and reader for EMV transactions
    const pairedDevice = getPairedDevice(seller);
    useEffect(() => {
        const loadStripe = async () => {
            const StripeTerminal = await loadStripeTerminal();
            if (!StripeTerminal) {
                return;
            }

            if (isConnectingGlobal) {
                return;
            }

            console.log("%cEMV: Initializing reader", "color:green", readerTerminal ?? "");
            const terminal = initializeReaderTerminal(StripeTerminal, seller);

            if (terminal && !hasConnected.current) {
                isConnectingGlobal = true;
                const location = seller.preferences.stripeTerminal.location?.remoteId;
                const config = { simulated: !!window.flags?.stripe?.simulateTerminal, location };
                if (config.simulated) {
                    console.log("%cEMV: Simulating Stripe Terminal", "color:blue");
                }

                const discoverResult = (await terminal.discoverReaders(config)) as DiscoverResult;
                const selectedReader = discoverResult.discoveredReaders.find((reader) => {
                    if (pairedDevice?.remoteId === reader.id) {
                        return true;
                    }

                    return config.simulated && reader.id === EMVTerminal.SIMULATED_DEVICE;
                });

                if (selectedReader && !hasConnected.current) {
                    console.log("%cEMV: Connecting to reader...", "color: green", selectedReader.label, {
                        terminal,
                        s: terminal.getConnectionStatus(),
                    });
                    const result = await terminal.connectReader(selectedReader);

                    if ("error" in result) {
                        console.error(result.error);
                        console.log("🚨EMV: Connection failed", { result, selectedReader, terminal });
                    } else {
                        useCreatePurchaseStore.getState().actions.setIsEMV(true);
                        console.log(
                            "✅EMV: Connection successful",
                            omit(selectedReader, ["action", "base_url", "metadata"]),
                        );
                        hasConnected.current = true;
                    }
                }

                readerTerminal = terminal;
                isConnectingGlobal = false;
            }
        };

        if (hasFeature(seller, FeatureRole.EMV)) {
            if (readerTerminal && isManual && pairedDevice) {
                useCreatePurchaseStore.getState().actions.setIsEMV(true);
            } else if (!readerTerminal && isStripeEnabled(seller)) {
                loadStripe();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seller, pairedDevice]);

    useEffect(() => {
        const isSimulated = window.flags?.stripe?.simulateTerminal;
        if (readerTerminal && !isManual && !pairedDevice && !isSimulated) {
            console.log("EMV: Disabling EMV because reader is not paired", {
                readerTerminal,
                isManual: !isManual,
                pairedDevice,
            });
            useCreatePurchaseStore.getState().actions.setIsEMV(false);
        }
    }, [isManual, pairedDevice]);

    return readerTerminal;
};

export const initializeReaderTerminal = (StripeTerminal: StripeTerminal, seller: Seller) => {
    return StripeTerminal.create({
        onFetchConnectionToken: async () => {
            const connectionTokenPath = `/sellers/${seller.id}/stripeTerminal/connectionToken`;
            const { data } = await api.get<StripeConnectionToken>(connectionTokenPath);
            return data.secret;
        },
        onUnexpectedReaderDisconnect: () => {
            console.log("%cEMV: Unexpectedly disconnected! Do something", "color: red");
        },
        onConnectionStatusChange: (response) => {
            console.log(`%cEMV: Status change ${response.status}`, "color: #8A8C91");
        },
    });
};

const getPairedDevice = (seller: Seller) => {
    const devices: EmvDevice[] = seller.preferences.stripeTerminal?.paymentDevices ?? [];
    const computer = JSON.parse(localStorage.getItem("computer") ?? "{}");
    const isSimulated = window.flags?.stripe?.simulateTerminal;
    if (computer.id) {
        return devices.find((d) => d.computer?.id === computer.id && !isSimulated);
    }

    return null;
};

export enum EMVTerminal {
    WAITING = "waiting_for_input",
    READER_CONNECTED = "connected",
    SIMULATED_DEVICE = "SIMULATOR",
}
